import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Check, X, CalendarIcon, CreditCard, Wallet, Clock } from 'lucide-react';
import { format } from 'date-fns';

const LEAVE_TYPES = [
  { value: 'paid', label: 'Paid Leave', desc: 'Deducted from your leave balance' },
  { value: 'unpaid', label: 'Unpaid Leave', desc: 'Salary deducted for these days' },
  { value: 'half_day', label: 'Half Day', desc: '0.5 day deducted from balance' },
];

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

const typeColors = {
  paid: 'bg-blue-50 text-[#002FA7] border-blue-200',
  unpaid: 'bg-orange-50 text-orange-700 border-orange-200',
  half_day: 'bg-violet-50 text-violet-700 border-violet-200',
  casual: 'bg-muted text-muted-foreground border-border',
  sick: 'bg-muted text-muted-foreground border-border',
  earned: 'bg-muted text-muted-foreground border-border',
};

const typeLabels = { paid: 'Paid', unpaid: 'Unpaid', half_day: 'Half Day', casual: 'Casual', sick: 'Sick', earned: 'Earned' };

export default function LeavesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(isAdmin ? 'pending' : 'all');
  const [applyOpen, setApplyOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: '', start_date: null, end_date: null, reason: '' });
  const [applying, setApplying] = useState(false);
  const [crediting, setCrediting] = useState(false);

  const fetchLeaves = useCallback(() => {
    const params = tab === 'all' ? '' : `?status=${tab}`;
    api.get(`/leaves${params}`)
      .then(res => setLeaves(res.data))
      .catch(() => toast.error('Failed to load leaves'))
      .finally(() => setLoading(false));
  }, [tab]);

  const fetchBalance = useCallback(() => {
    api.get('/leaves/balance')
      .then(res => setBalance(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchLeaves(); fetchBalance(); }, [fetchLeaves, fetchBalance]);

  const handleApply = async () => {
    if (!form.leave_type || !form.start_date || !form.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    if (form.leave_type !== 'half_day' && !form.end_date) {
      toast.error('Please select end date');
      return;
    }
    setApplying(true);
    try {
      await api.post('/leaves', {
        leave_type: form.leave_type,
        start_date: format(form.start_date, 'yyyy-MM-dd'),
        end_date: form.leave_type === 'half_day' ? format(form.start_date, 'yyyy-MM-dd') : format(form.end_date, 'yyyy-MM-dd'),
        reason: form.reason,
        is_half_day: form.leave_type === 'half_day',
      });
      toast.success('Leave request submitted');
      setApplyOpen(false);
      setForm({ leave_type: '', start_date: null, end_date: null, reason: '' });
      fetchLeaves();
      fetchBalance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const handleAction = async (leaveId, action) => {
    try {
      await api.put(`/leaves/${leaveId}/${action}`);
      toast.success(`Leave ${action}d`);
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleCreditLeaves = async () => {
    setCrediting(true);
    try {
      const res = await api.post('/leaves/credit-monthly');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to credit leaves');
    } finally {
      setCrediting(false);
    }
  };

  return (
    <div data-testid="leaves-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">
            {isAdmin ? 'Leave Management' : 'My Leaves'}
          </h2>
          <p className="text-sm text-muted-foreground font-body mt-1">
            {isAdmin ? 'Review and manage employee leave requests' : 'Apply and track your leave requests'}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={handleCreditLeaves} disabled={crediting} variant="outline" className="rounded-sm" data-testid="credit-leaves-btn">
              <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {crediting ? 'Crediting...' : 'Credit Monthly Leaves'}
            </Button>
          )}
          {!isAdmin && (
            <Button onClick={() => setApplyOpen(true)} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="apply-leave-btn">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Apply Leave
            </Button>
          )}
        </div>
      </div>

      {/* Leave Balance Cards */}
      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-border bg-card rounded-sm p-4" data-testid="balance-available">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Available Balance</span>
            </div>
            <p className="text-2xl font-heading font-bold text-[#002FA7]">{balance.paid_balance?.toFixed(2)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">days</p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4" data-testid="balance-credited">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Total Credited</span>
            </div>
            <p className="text-2xl font-heading font-bold text-foreground">{balance.total_credited?.toFixed(2)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">1.25/month</p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Paid Used</span>
            </div>
            <p className="text-2xl font-heading font-bold text-foreground">{balance.total_used_paid?.toFixed(1)}</p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-orange-500" strokeWidth={1.5} />
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Unpaid Used</span>
            </div>
            <p className="text-2xl font-heading font-bold text-foreground">{balance.total_used_unpaid?.toFixed(1)}</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 rounded-sm">
          {isAdmin && <TabsTrigger value="pending" className="rounded-sm" data-testid="tab-pending">Pending</TabsTrigger>}
          <TabsTrigger value="all" className="rounded-sm" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="approved" className="rounded-sm" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" className="rounded-sm" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          <div className="border border-border bg-card rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>}
                  <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">From</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">To</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Days</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  {isAdmin && <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isAdmin ? 8 : 6} className="text-center text-muted-foreground py-8">Loading...</TableCell></TableRow>
                ) : leaves.length === 0 ? (
                  <TableRow><TableCell colSpan={isAdmin ? 8 : 6} className="text-center text-muted-foreground py-8">No leave requests</TableCell></TableRow>
                ) : leaves.map(leave => (
                  <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                    {isAdmin && <TableCell className="font-medium">{leave.user_name}<span className="text-muted-foreground text-xs ml-1 font-mono">{leave.employee_id}</span></TableCell>}
                    <TableCell>
                      <Badge className={`${typeColors[leave.leave_type] || typeColors.casual} rounded-sm text-xs`}>
                        {typeLabels[leave.leave_type] || leave.leave_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{leave.start_date}</TableCell>
                    <TableCell className="font-mono text-xs">{leave.end_date}</TableCell>
                    <TableCell className="font-mono text-xs">{leave.days ?? '-'}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{leave.reason}</TableCell>
                    <TableCell><Badge className={`${statusColors[leave.status]} capitalize text-xs`}>{leave.status}</Badge></TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {leave.status === 'pending' && (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => handleAction(leave.id, 'approve')} className="text-emerald-600 hover:bg-emerald-50" data-testid={`approve-leave-${leave.id}`}>
                              <Check className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleAction(leave.id, 'reject')} className="text-red-500 hover:bg-red-50" data-testid={`reject-leave-${leave.id}`}>
                              <X className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Apply Leave Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Apply for Leave</DialogTitle>
            <DialogDescription>
              Available balance: <span className="font-mono font-bold text-[#002FA7]">{balance?.paid_balance?.toFixed(2) || 0}</span> days
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Leave Type</Label>
              <Select value={form.leave_type} onValueChange={v => setForm(p => ({ ...p, leave_type: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="leave-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div>
                        <span className="font-medium">{t.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">{t.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.leave_type === 'half_day' ? (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`w-full justify-start text-left font-normal rounded-sm ${!form.start_date ? 'text-muted-foreground' : ''}`} data-testid="leave-start-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.start_date ? format(form.start_date, 'PP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.start_date} onSelect={d => setForm(p => ({ ...p, start_date: d }))} />
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={`w-full justify-start text-left font-normal rounded-sm ${!form.start_date ? 'text-muted-foreground' : ''}`} data-testid="leave-start-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.start_date ? format(form.start_date, 'PP') : 'Start'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={form.start_date} onSelect={d => setForm(p => ({ ...p, start_date: d }))} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={`w-full justify-start text-left font-normal rounded-sm ${!form.end_date ? 'text-muted-foreground' : ''}`} data-testid="leave-end-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.end_date ? format(form.end_date, 'PP') : 'End'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={form.end_date} onSelect={d => setForm(p => ({ ...p, end_date: d }))} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
              <Input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="Brief description" className="rounded-sm" data-testid="leave-reason-input" />
            </div>

            {form.leave_type === 'paid' && balance && (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-[#002FA7]">
                This will be deducted from your paid leave balance ({balance.paid_balance?.toFixed(2)} remaining)
              </div>
            )}
            {form.leave_type === 'unpaid' && (
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-xs text-orange-700">
                Salary will be deducted proportionally for unpaid leave days
              </div>
            )}
            {form.leave_type === 'half_day' && (
              <div className="bg-violet-50 border border-violet-200 rounded-sm p-3 text-xs text-violet-700">
                0.5 day will be deducted from your paid leave balance
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleApply} disabled={applying} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="submit-leave-btn">
              {applying ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
